import { countWorkingDaysInOverlap, WORK_HOURS_PER_DAY } from "./billing-calculations";
import { parseISODate } from "./format";

/**
 * Pure calculations for employee KPIs in VIB-1634 and VIB-1454.
 *
 * Result KPIs use projects completed in the selected calendar year and all
 * lifetime hours on those projects. Operational KPIs use active projects or
 * time entries inside the selected calendar year, as documented per type.
 */

export interface EmployeeKpiProject {
  id: string;
  name: string;
  projectCode: string | null;
  projectStatus: string | null;
  completedAt: string | null;
  createdAt?: string | null;
  acceptedAt?: string | null;
  isInternal: boolean;
  bfActual: number | null;
  /** Estimert GEEK fra tilbudet. Brukes for pågående prosjekter i salgs-GEEK-en. */
  bfEstimated?: number | null;
  productionLeadId?: string | null;
}

export interface EmployeeKpiTimeEntry {
  projectId: string | null;
  userId: string | null;
  durationSeconds: number | null;
  startDate: string | null;
}

export interface EmployeeKpiAssignment {
  userId: string;
  projectId: string;
  /** Sold/allocated hours. External cost lines are filtered by the hook. */
  hours: number | null;
}

export interface EmployeeKpiProjectOwner {
  projectId: string;
  userId: string;
  ownershipPct: number | null;
}

export interface EmployeeKpiBudgetLine {
  projectId: string;
  quantityHours: number | null;
}

export interface EmployeeKpiAbsence {
  userId: string;
  type: string;
  status: string;
  startDate: string;
  endDate: string;
  hours: number | null;
}

export interface CompletedProjectKpiRow {
  projectId: string;
  projectName: string;
  projectCode: string | null;
  completedAt: string;
  employeeHours: number;
  totalProjectHours: number;
  geekShare: number;
  actualHourlyRate: number;
}

export interface SoldVsLoggedProjectRow {
  projectId: string;
  projectName: string;
  projectCode: string | null;
  soldHours: number;
  loggedHours: number;
  /** Sold hours / logged hours × 100. Null before any hours are logged. */
  ratioPct: number | null;
}

export interface InternalProjectHoursRow {
  projectId: string;
  projectName: string;
  projectCode: string | null;
  hours: number;
}

export interface EmployeeKpis {
  year: number;
  completed: {
    actualGeekShare: number;
    employeeHours: number;
    actualHourlyRate: number | null;
    organizationHourlyRate: number | null;
    deltaVsOrganizationPct: number | null;
    projects: CompletedProjectKpiRow[];
  };
  soldVsLogged: {
    soldHours: number;
    loggedHours: number;
    ratioPct: number | null;
    projects: SoldVsLoggedProjectRow[];
  };
  internalTime: {
    hours: number;
    totalLoggedHours: number;
    sharePct: number | null;
    projects: InternalProjectHoursRow[];
  };
  sales: {
    attributedGeek: number;
    projectCount: number;
    projects: SalesProjectRow[];
  };
  roleDeviation: {
    salesOwner: RoleDeviationSummary;
    projectLead: RoleDeviationSummary;
  };
  sickLeave: {
    hours: number;
    days: number;
    entryCount: number;
    entries: SickLeaveRow[];
  };
}

export interface SalesProjectRow {
  projectId: string;
  projectName: string;
  projectCode: string | null;
  closedAt: string;
  /** Reell GEEK når prosjektet er avsluttet, ellers estimert. */
  geek: number;
  isActual: boolean;
  ownerSharePct: number;
  attributedGeek: number;
}

export interface SickLeaveRow {
  startDate: string;
  endDate: string;
  hours: number;
  status: string;
}

export interface RoleDeviationProjectRow {
  projectId: string;
  projectName: string;
  projectCode: string | null;
  /** Eierandel som salgseier, 1 som prosjektleder. */
  weight: number;
  soldHours: number;
  loggedHours: number;
  deviationPct: number | null;
}

export interface RoleDeviationSummary {
  soldHours: number;
  loggedHours: number;
  deviationPct: number | null;
  projectCount: number;
  projects: RoleDeviationProjectRow[];
}

function hoursFromSeconds(seconds: number | null): number {
  return Math.max(0, Number(seconds ?? 0)) / 3600;
}

function roundHours(hours: number): number {
  return Math.round(hours * 100) / 100;
}

function roundPct(value: number): number {
  return Math.round(value * 10) / 10;
}

function ratioPct(numerator: number, denominator: number): number | null {
  if (denominator <= 0) return null;
  return roundPct((numerator / denominator) * 100);
}

function isInCalendarYear(value: string | null, year: number): boolean {
  if (!value) return false;
  const date = new Date(value);
  return !Number.isNaN(date.getTime()) && date.getUTCFullYear() === year;
}

export function calculateEmployeeKpis(input: {
  userId: string;
  year: number;
  projects: EmployeeKpiProject[];
  timeEntries: EmployeeKpiTimeEntry[];
  assignments: EmployeeKpiAssignment[];
  projectOwners?: EmployeeKpiProjectOwner[];
  budgetLines?: EmployeeKpiBudgetLine[];
  absences?: EmployeeKpiAbsence[];
  asOfDate?: string;
}): EmployeeKpis {
  const {
    userId,
    year,
    projects,
    timeEntries,
    assignments,
    projectOwners = [],
    budgetLines = [],
    absences = [],
    asOfDate = `${year}-12-31`,
  } = input;
  const projectById = new Map(projects.map((project) => [project.id, project]));

  const employeeHoursByProject = new Map<string, number>();
  const totalHoursByProject = new Map<string, number>();
  let totalEmployeeHoursInYear = 0;

  for (const entry of timeEntries) {
    const hours = hoursFromSeconds(entry.durationSeconds);
    if (hours <= 0) continue;

    if (entry.projectId) {
      totalHoursByProject.set(
        entry.projectId,
        (totalHoursByProject.get(entry.projectId) ?? 0) + hours,
      );
      if (entry.userId === userId) {
        employeeHoursByProject.set(
          entry.projectId,
          (employeeHoursByProject.get(entry.projectId) ?? 0) + hours,
        );
      }
    }

    if (
      entry.userId === userId &&
      entry.projectId != null &&
      projectById.has(entry.projectId) &&
      isInCalendarYear(entry.startDate, year)
    ) {
      totalEmployeeHoursInYear += hours;
    }
  }

  const completedProjects = projects.filter(
    (project) =>
      !project.isInternal &&
      project.projectStatus === "completed" &&
      isInCalendarYear(project.completedAt, year),
  );

  const completedRows: CompletedProjectKpiRow[] = [];
  let employeeGeekShareRaw = 0;
  let employeeCompletedHoursRaw = 0;
  let organizationGeekRaw = 0;
  let organizationHoursRaw = 0;

  for (const project of completedProjects) {
    const totalProjectHours = totalHoursByProject.get(project.id) ?? 0;
    if (totalProjectHours <= 0) continue;

    const bfActual = Number(project.bfActual ?? 0);
    organizationGeekRaw += bfActual;
    organizationHoursRaw += totalProjectHours;

    const employeeHours = employeeHoursByProject.get(project.id) ?? 0;
    if (employeeHours <= 0) continue;

    const geekShare = bfActual * (employeeHours / totalProjectHours);
    employeeGeekShareRaw += geekShare;
    employeeCompletedHoursRaw += employeeHours;
    completedRows.push({
      projectId: project.id,
      projectName: project.name,
      projectCode: project.projectCode,
      completedAt: project.completedAt as string,
      employeeHours: roundHours(employeeHours),
      totalProjectHours: roundHours(totalProjectHours),
      geekShare: Math.round(geekShare),
      actualHourlyRate: Math.round(bfActual / totalProjectHours),
    });
  }
  completedRows.sort((a, b) => b.geekShare - a.geekShare);

  const actualHourlyRate =
    employeeCompletedHoursRaw > 0
      ? Math.round(employeeGeekShareRaw / employeeCompletedHoursRaw)
      : null;
  const organizationHourlyRate =
    organizationHoursRaw > 0 ? Math.round(organizationGeekRaw / organizationHoursRaw) : null;
  const deltaVsOrganizationPct =
    actualHourlyRate != null && organizationHourlyRate != null && organizationHourlyRate > 0
      ? roundPct(((actualHourlyRate - organizationHourlyRate) / organizationHourlyRate) * 100)
      : null;

  const soldHoursByProject = new Map<string, number>();
  for (const assignment of assignments) {
    if (assignment.userId !== userId) continue;
    const project = projectById.get(assignment.projectId);
    if (!project || project.isInternal || project.projectStatus !== "active") continue;
    soldHoursByProject.set(
      assignment.projectId,
      (soldHoursByProject.get(assignment.projectId) ?? 0) +
        Math.max(0, Number(assignment.hours ?? 0)),
    );
  }

  const activeProjectIds = new Set<string>();
  for (const project of projects) {
    if (!project.isInternal && project.projectStatus === "active") activeProjectIds.add(project.id);
  }
  const relevantActiveIds = new Set<string>();
  for (const projectId of activeProjectIds) {
    if ((soldHoursByProject.get(projectId) ?? 0) > 0 || (employeeHoursByProject.get(projectId) ?? 0) > 0) {
      relevantActiveIds.add(projectId);
    }
  }

  const soldVsLoggedRows: SoldVsLoggedProjectRow[] = [];
  let soldHoursRaw = 0;
  let loggedHoursRaw = 0;
  for (const projectId of relevantActiveIds) {
    const project = projectById.get(projectId);
    if (!project) continue;
    const soldHours = soldHoursByProject.get(projectId) ?? 0;
    const loggedHours = employeeHoursByProject.get(projectId) ?? 0;
    soldHoursRaw += soldHours;
    loggedHoursRaw += loggedHours;
    soldVsLoggedRows.push({
      projectId,
      projectName: project.name,
      projectCode: project.projectCode,
      soldHours: roundHours(soldHours),
      loggedHours: roundHours(loggedHours),
      ratioPct: ratioPct(soldHours, loggedHours),
    });
  }
  soldVsLoggedRows.sort((a, b) => a.projectName.localeCompare(b.projectName, "nb"));

  const internalHoursByProject = new Map<string, number>();
  for (const entry of timeEntries) {
    if (entry.userId !== userId || !entry.projectId || !isInCalendarYear(entry.startDate, year)) {
      continue;
    }
    const project = projectById.get(entry.projectId);
    if (!project?.isInternal) continue;
    internalHoursByProject.set(
      entry.projectId,
      (internalHoursByProject.get(entry.projectId) ?? 0) + hoursFromSeconds(entry.durationSeconds),
    );
  }

  const internalRows: InternalProjectHoursRow[] = [];
  let internalHoursRaw = 0;
  for (const [projectId, hours] of internalHoursByProject) {
    if (hours <= 0) continue;
    const project = projectById.get(projectId);
    if (!project) continue;
    internalHoursRaw += hours;
    internalRows.push({
      projectId,
      projectName: project.name,
      projectCode: project.projectCode,
      hours: roundHours(hours),
    });
  }
  internalRows.sort((a, b) => b.hours - a.hours);

  const ownerShareByProject = new Map<string, number>();
  for (const owner of projectOwners) {
    if (owner.userId !== userId) continue;
    ownerShareByProject.set(
      owner.projectId,
      (ownerShareByProject.get(owner.projectId) ?? 0) +
        Math.max(0, Number(owner.ownershipPct ?? 0)) / 100,
    );
  }

  let attributedSalesRaw = 0;
  const salesRows: SalesProjectRow[] = [];
  for (const project of projects) {
    if (project.isInternal) continue;
    const ownerShare = ownerShareByProject.get(project.id) ?? 0;
    const closedAt = project.acceptedAt ?? project.createdAt ?? null;
    if (ownerShare <= 0 || !isInCalendarYear(closedAt, year)) continue;
    // Reell GEEK når prosjektet er avsluttet, ellers estimert. Kansellerte
    // salg teller ikke. Samme splitt som GEEK-mål per selger (VIB-3028).
    if (project.projectStatus === "cancelled") continue;
    const isActual = project.projectStatus === "completed";
    const geek = Number((isActual ? project.bfActual : project.bfEstimated) ?? 0);
    attributedSalesRaw += geek * ownerShare;
    salesRows.push({
      projectId: project.id,
      projectName: project.name,
      projectCode: project.projectCode,
      closedAt: closedAt as string,
      geek: Math.round(geek),
      isActual,
      ownerSharePct: roundPct(ownerShare * 100),
      attributedGeek: Math.round(geek * ownerShare),
    });
  }
  salesRows.sort((a, b) => b.attributedGeek - a.attributedGeek);

  const soldHoursByCompletedProject = new Map<string, number>();
  for (const line of budgetLines) {
    soldHoursByCompletedProject.set(
      line.projectId,
      (soldHoursByCompletedProject.get(line.projectId) ?? 0) +
        Math.max(0, Number(line.quantityHours ?? 0)),
    );
  }

  function summarizeRoleProjects(role: "salesOwner" | "projectLead"): RoleDeviationSummary {
    let soldHours = 0;
    let loggedHours = 0;
    const rows: RoleDeviationProjectRow[] = [];
    for (const project of completedProjects) {
      const weight = role === "salesOwner"
        ? ownerShareByProject.get(project.id) ?? 0
        : project.productionLeadId === userId ? 1 : 0;
      if (weight <= 0) continue;
      const projectSold = soldHoursByCompletedProject.get(project.id) ?? 0;
      const projectLogged = totalHoursByProject.get(project.id) ?? 0;
      soldHours += projectSold * weight;
      loggedHours += projectLogged * weight;
      rows.push({
        projectId: project.id,
        projectName: project.name,
        projectCode: project.projectCode,
        weight,
        soldHours: roundHours(projectSold),
        loggedHours: roundHours(projectLogged),
        deviationPct: projectSold > 0
          ? roundPct(((projectLogged - projectSold) / projectSold) * 100)
          : null,
      });
    }
    rows.sort((a, b) => (b.deviationPct ?? -Infinity) - (a.deviationPct ?? -Infinity));
    return {
      soldHours: roundHours(soldHours),
      loggedHours: roundHours(loggedHours),
      deviationPct: soldHours > 0
        ? roundPct(((loggedHours - soldHours) / soldHours) * 100)
        : null,
      projectCount: rows.length,
      projects: rows,
    };
  }

  const yearStart = parseISODate(`${year}-01-01`);
  const requestedEnd = parseISODate(asOfDate.slice(0, 10));
  const yearEnd = parseISODate(`${year}-12-31`);
  const periodEnd = requestedEnd < yearEnd ? requestedEnd : yearEnd;
  let sickHoursRaw = 0;
  const sickRows: SickLeaveRow[] = [];
  for (const absence of absences) {
    if (
      absence.userId !== userId ||
      absence.type !== "sick" ||
      absence.status === "rejected"
    ) {
      continue;
    }
    const start = parseISODate(absence.startDate);
    const end = parseISODate(absence.endDate);
    if (end < yearStart || start > periodEnd) continue;
    // absences.hours er deldagsmarkør: trekkes én gang, ikke per dag.
    const hours = absence.hours != null
      ? (start >= yearStart && start <= periodEnd ? Math.max(0, Number(absence.hours)) : 0)
      : countWorkingDaysInOverlap(start, end, yearStart, periodEnd) * WORK_HOURS_PER_DAY;
    sickHoursRaw += hours;
    sickRows.push({
      startDate: absence.startDate,
      endDate: absence.endDate,
      hours: roundHours(hours),
      status: absence.status,
    });
  }
  sickRows.sort((a, b) => b.startDate.localeCompare(a.startDate));

  return {
    year,
    completed: {
      actualGeekShare: Math.round(employeeGeekShareRaw),
      employeeHours: roundHours(employeeCompletedHoursRaw),
      actualHourlyRate,
      organizationHourlyRate,
      deltaVsOrganizationPct,
      projects: completedRows,
    },
    soldVsLogged: {
      soldHours: roundHours(soldHoursRaw),
      loggedHours: roundHours(loggedHoursRaw),
      ratioPct: ratioPct(soldHoursRaw, loggedHoursRaw),
      projects: soldVsLoggedRows,
    },
    internalTime: {
      hours: roundHours(internalHoursRaw),
      totalLoggedHours: roundHours(totalEmployeeHoursInYear),
      sharePct: ratioPct(internalHoursRaw, totalEmployeeHoursInYear),
      projects: internalRows,
    },
    sales: {
      attributedGeek: Math.round(attributedSalesRaw),
      projectCount: salesRows.length,
      projects: salesRows,
    },
    roleDeviation: {
      salesOwner: summarizeRoleProjects("salesOwner"),
      projectLead: summarizeRoleProjects("projectLead"),
    },
    sickLeave: {
      hours: roundHours(sickHoursRaw),
      days: roundHours(sickHoursRaw / WORK_HOURS_PER_DAY),
      entryCount: sickRows.length,
      entries: sickRows,
    },
  };
}
